<template>
  <div v-if="isLoading" class="text-center mt-5">
    <LoadingSpinner text="Loading topic data..." />
  </div>
  <div v-else-if="topicData" class="mx-4">
    <div class="flex items-center space-x-5 mb-6 mt-2">
      <span class="font-semibold bg-indigo-50 dark:bg-indigo-900/30 dark:text-indigo-300 px-2 py-1">
        Part {{ topicData?.partId }}
      </span>
      <div class="text-xl font-bold dark:text-gray-100">{{ topicData?.topicName }}</div>
    </div>
    <div class="bg-indigo-50 dark:bg-gray-800 p-5 relative">
      <div class="block absolute right-0 top-0 -translate-y-[100%]">
        <button
          @click="onPrevQuestion"
          :class="[
            currentQuestionIndex === 0 ? 'disabled text-transparent' : '',
            'text-indigo-600 dark:text-indigo-400 text-xl',
          ]"
        >
          [上一题]
        </button>
        <button
          :class="[
            currentQuestionIndex === topicData.questionList.length - 1
              ? 'disabled text-transparent'
              : '',
            'text-indigo-600 dark:text-indigo-400 text-xl',
          ]"
          @click="onNextQuestion"
        >
          [下一题]
        </button>
      </div>
      <div class="flex justify-between">
        <span class="text-lg sm:text-xl font-semibold dark:text-gray-100">
          {{ currentQuestionIndex + 1 }}.
          <span class="whitespace-pre-wrap">
            {{ curQuestion }}
          </span>
        </span>
      </div>
      <div class="flex justify-between items-center">
        <Tabs
          :init-tab-id="1"
          :tabs="answerVersionTab"
          @cur-tab-Id="setCurAnswerVersion"
          class="mt-3"
        ></Tabs>
        <LoadingBtn
          text="生成音频"
          loading-text="生成中..."
          :is-loading="isGeneratingSpeech"
          @click="generateAudio"
        ></LoadingBtn>
      </div>
      <AudioPlayer ref="audioPlayerRef" v-if="audioUrl" :audio-url="audioUrl" />
      <div class="mt-2">
        <AiModelAnswer
          ref="AiModelAnswerRef"
          :question="curQuestion ?? ''"
          @post-answer="postAnswerAndNotes"
          :history-answer="plainAnswerText"
          :user-ideas="userIdeas"
          :part-id="topicData?.partId"
          :conversation-context="[]"
        ></AiModelAnswer>
      </div>
    </div>
  </div>
  <div v-else class="text-center mt-5 text-red-500 dark:text-red-400">
    Failed to load topic data. Please try again.
  </div>
  
</template>

<script setup lang="ts">
import Tabs from "@/components/ui/Tabs.vue";
import { useRoute, useRouter } from "vue-router";
import type { Topic } from "@/types/topic/topic";
import AiModelAnswer from "@/components/speaking/aiModelAnswer/AiModelAnswer.vue";
import { computed, ref, type ComputedRef } from "vue";
import { fetchTopicByIdAPI } from "@/api/speaking/topic";
import type { OralModelAnswer } from "@/types/topic/oralAnswer";
import {
  AnswerVersion,
  AccessType,
  ModelAnswerVersion,
} from "@/types/topic/oralAnswerEnum";
import AudioPlayer from "@/components/common/AudioPlayer.vue";
import {
  fetchFreeModelAnswerApi,
  generateModelAnswerSpeechApi,
  postOralModelAnswerApi,
} from "@/api/speaking/Answers";
import { HttpStatus } from "@/types/axios/httpStatus";
import toast from "@/utils/toaster"
import { watchEffect } from "vue";
import { onMounted } from "vue";
import LoadingSpinner from "@/components/ui/LoadingSpinner.vue";
import { PlusIcon, PencilIcon, ArrowTopRightOnSquareIcon } from "@heroicons/vue/24/outline";
import { Switch } from "@headlessui/vue";
import LoadingBtn from "@/components/ui/LoadingBtn.vue";

const router = useRouter();
const route = useRoute();
const isLoading = ref(false);
const topicData = ref<Topic | null>(null);
const currentQuestionIndex = ref(Number(route.query.q) || 0); // Initialize with default value
const AiModelAnswerRef = ref<InstanceType<typeof AiModelAnswer> | null>(null);
const audioPlayerRef = ref<InstanceType<typeof AudioPlayer> | null>(null);

const answerVersionTab = [
  {
    name: "6.5+ 版本",
    id: AnswerVersion.FREE,
    current: true,
  },
  {
    name: "7.5+ 版本",
    id: AnswerVersion.VIP,
    current: true,
  },
];

const modelAnswers = ref<Record<ModelAnswerVersion, OralModelAnswer>>({
  [AnswerVersion.FREE]: {
    answer: "",
    userIdeas: "",
    accessType: AccessType.FREE,
    notes: "",
    hasAnswer: false,
    audioUrl: "",
  },
  [AnswerVersion.VIP]: {
    answer: "",
    userIdeas: "",
    accessType: AccessType.VIP,
    notes: "",
    hasAnswer: false,
    audioUrl: "",
  },
});

const curAnswerVersion = ref(ModelAnswerVersion.FREE);

const curQuestion = computed(() => {
  if (!topicData.value?.questionList?.length || currentQuestionIndex.value < 0) return '';
  return topicData.value.questionList[currentQuestionIndex.value]?.question || '';
});

const curQuestionId = computed(() => {
  if (!topicData.value?.questionList?.length || currentQuestionIndex.value < 0) return '';
  return topicData.value.questionList[currentQuestionIndex.value]?.questionId || '';
});

const plainAnswerText = computed(
  () => modelAnswers.value[curAnswerVersion.value].answer ?? ""
);
const userIdeas = computed(
  () => modelAnswers.value[curAnswerVersion.value].userIdeas ?? ""
);

const audioUrl = computed(
  () => modelAnswers.value[curAnswerVersion.value].audioUrl ?? ""
);

const oralTopicId = route.params.oralTopicId as string;

// Add console logs and error handling
console.log('Route params:', route.params);
console.log('Query:', route.query);

const setCurAnswerVersion = (version: ModelAnswerVersion) => {
  curAnswerVersion.value = version;
};

const fetchTopicData = async () => {
  try {
    isLoading.value = true;
    console.log('Fetching topic data for ID:', oralTopicId);
    const response = await fetchTopicByIdAPI(oralTopicId);
    console.log('API Response:', response);
    
    if (response.data) {
      topicData.value = response.data;
      await fetchFreeModelAnswer(); // Fetch model answer after topic data is loaded
      console.log('Topic data set:', topicData.value);
    } else {
      toast.error('Failed to load topic data');
    }
  } catch (error) {
    console.error('Error fetching topic:', error);
    toast.error('Error loading topic data');
  } finally {
    isLoading.value = false;
  }
};

// fetch and post model answer logic
const fetchFreeModelAnswer = async () => {
  if (!curQuestionId.value) {
    console.log('No question ID available');
    return;
  }
  try {
    const response = await fetchFreeModelAnswerApi(curQuestionId.value);
    if (response?.data) {
      modelAnswers.value[ModelAnswerVersion.FREE] = response.data;
    }
  } catch (error) {
    console.error('Error fetching model answer:', error);
    toast.error('Failed to load model answer');
  }
};

// Remove watchEffect and use onMounted only
onMounted(() => {
  fetchTopicData();
});

// Update navigation logic
const onPrevQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    currentQuestionIndex.value--;
    router.push({
      path: `/admin/topic/question-manager/${oralTopicId}`,
      query: { q: currentQuestionIndex.value.toString() }
    });
    AiModelAnswerRef.value?.reset();
    audioPlayerRef.value?.resetAudio();
    fetchFreeModelAnswer();
  }
};

const onNextQuestion = () => {
  if (
    topicData.value?.questionList &&
    currentQuestionIndex.value < topicData.value.questionList.length - 1
  ) {
    currentQuestionIndex.value++;
    router.push({
      path: `/admin/topic/question-manager/${oralTopicId}`,
      query: { q: currentQuestionIndex.value.toString() }
    });
    AiModelAnswerRef.value?.reset();
    audioPlayerRef.value?.resetAudio();
    fetchFreeModelAnswer();
  }
};

// audio generation logic

const isGeneratingSpeech = ref(false);
const generateAudio = async () => {
  isGeneratingSpeech.value = true;
  if (!curQuestionId.value) return;
  const res = await generateModelAnswerSpeechApi(
    curQuestionId.value,
    AccessType.FREE
  );
  if (res.status === HttpStatus.HTTP_200_AUDIO_EXISTS) {
    toast.info("音频已存在！");
    isGeneratingSpeech.value = false;
    return;
  }
  if (res.status === HttpStatus.HTTP_400_COQUI_TTS_ERROR) {
    isGeneratingSpeech.value = false;
    return toast.error("音频服务异常, 请稍后重试！");
  }
  if (res.status !== HttpStatus.HTTP_200_OK) {
    toast.error("生成音频失败, 请重新登录再试！");
    isGeneratingSpeech.value = false;
    return;
  }
  toast.success("生成音频成功！");
  isGeneratingSpeech.value = false;
  console.log(res.data.audioUrl);
  modelAnswers.value[curAnswerVersion.value].audioUrl =
    res.data.audioUrl + `?${Date.now()}`; // 添加时间戳，防止浏览器使用缓存
  setTimeout(() => {
    audioPlayerRef.value?.playAudio();
  }, 1000);
};

const postAnswerAndNotes = async (
  userIdeas: string,
  plainAnswerText: string
) => {
  if (!curQuestionId.value) return;
  const res = await postOralModelAnswerApi(
    curQuestionId.value,
    userIdeas,
    plainAnswerText,
    Number(curAnswerVersion.value)
  );
  if (res.status !== HttpStatus.HTTP_200_OK) {
    return toast.error("记录同步失败, 请重新登录再试！");
  }
};

const openAddQuestionModal = () => {
  // Implementation of openAddQuestionModal function
};

const onVisibilityChange = (questionId: string, isVisible: boolean) => {
  // Implementation of onVisibilityChange function
};

const editQuestion = (question: any) => {
  // Implementation of editQuestion function
};

const navigateToQuestion = (topicId: string, questionId: string) => {
  // Implementation of navigateToQuestion function
};

interface Question {
  questionId: string;
  question: string;
  topicId: string;
  topicName: string;
  partId: number;
  categoryName: string;
  isVisible: boolean;
}

const questions = ref<Question[]>([]);
</script>
